import './App.css';
import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { Routes, Route, useLocation } from 'react-router-dom';

// Component imports
import Header from './components/layout/Header';
import About from './sections/About';
import Projects from './sections/Projects';
import Skills from './sections/Skills';
import Contact from './sections/Contact';
import Experience from './sections/Experience';
import ScrollToTopButton from './components/layout/ScrollToTopButton';
import Footer from './components/layout/Footer';
import Education from './sections/Education';
import Innovations from './sections/Innovations';
import Blog from './sections/Blog';
import BlogPost from './pages/BlogPost';
import { ThemeProvider } from './context/ThemeContext';

// Google Analytics configuration
const TRACKING_ID = "G-Y40QH7RNJL";
ReactGA.initialize(TRACKING_ID);

// Main portfolio layout with tabs
function PortfolioLayout() {
  const location = useLocation();
  // State for active tab management — check if navigated back from blog post
  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.activeTab || 'experience';
  });

  // Helper to scroll to the tab navigation anchor or a specific blog card
  const scrollToTabAnchor = (behavior = "smooth", targetSlug = null) => {
    if (targetSlug) {
      const card = document.getElementById(`blog-card-${targetSlug}`);
      if (card) {
        const headerOffset = 80;
        const cardPosition = card.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: cardPosition,
          behavior
        });
        return;
      }
    }

    const anchor = document.getElementById('tab-scroll-anchor');
    if (anchor) {
      const elementPosition = anchor.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition,
        behavior
      });
    }
  };

  // Scroll to section when navigated with activeTab state (e.g. back from blog post)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      const timer = setTimeout(() => {
        scrollToTabAnchor("smooth", location.state.targetSlug);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Track virtual pageviews in Google Analytics whenever the active tab changes
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: `/${activeTab}` });
  }, [activeTab]);

  // Navigation tabs configuration
  const navigationTabs = [
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'innovations', label: 'Innovations' },
    { id: 'education', label: 'Education' },
    { id: 'contact', label: 'Contact' },
    { id: 'blog', label: 'Blog' }
  ];

  // Function to handle tab change and scrolling
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    // Use a small timeout to ensure state update has processed
    setTimeout(() => {
      scrollToTabAnchor("smooth");
    }, 10);
  };

  // Render the active section based on selected tab
  const renderActiveSection = () => {
    switch (activeTab) {
      case 'experience':
        return <Experience />;
      case 'skills':
        return <Skills />;
      case 'projects':
        return <Projects />;
      case 'innovations':
        return <Innovations />;
      case 'education':
        return <Education />;
      case 'contact':
        return <Contact />;
      case 'blog':
        return <Blog />;
      default:
        return <Experience />;
    }
  };

  return (
    <div>
      <Header setActiveTab={handleTabChange} />
      <About setActiveTab={handleTabChange} />

      {/* Tab Navigation */}
      <div id="tab-scroll-anchor" className="invisible h-0 w-full" aria-hidden="true" />
      <div id="sections-container" className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md py-5 sm:py-4 sticky top-0 z-50 shadow-sm transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-2 sm:px-4">
          <LayoutGroup>
            <div className="grid grid-cols-4 sm:flex sm:justify-center gap-1 sm:gap-6">
              {navigationTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`relative px-2 sm:px-4 py-2 font-medium text-xs sm:text-sm transition-all whitespace-nowrap
            ${activeTab === tab.id
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </LayoutGroup>
        </div>
      </div>

      {/* Content Section */}
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderActiveSection()}
          </motion.div>
        </AnimatePresence>
      </div>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Routes>
        <Route path="/" element={<PortfolioLayout />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;